import { Formik } from 'formik';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { date, number, object } from 'yup';
import { PrimaryButton } from '../../../../components/Buttons/PrimaryButton';
import { SecondaryButton } from '../../../../components/Buttons/SecondaryButton';
import { Form, FormInputControl, FormMobisPicker } from '../../../../components/forms';
import { CenteredLoadingIndicator } from '../../../../components/LoadingIndicator';
import ServerValidationBox from '../../../../components/ServerValidationBox';
import { ButtonSizeEnum } from '../../../../constants';
import { PHRTrackerTemperatureDTO, TemperatureTypeEnum } from '../../../../generated';
import { createForPhrSection, updateForPhrSection } from '../../../../store/root-creator';
import { AppState } from '../../../../store/root-reducers';
import { ReduxPHRCategoryEnum } from '../../../../store/types';
import { convertTemperature } from '../../../../utils/conversionHelpers';
import { ButtonRow } from '../../components/StyledPhrItems';

interface IProps {
  selectedData: PHRTrackerTemperatureDTO | null;
  patientID: string;
  toggleView: () => void;
  unit: TemperatureTypeEnum;
  unitSelector: any;
}

interface IForm {
  trackerDate: moment.Moment;
  temperature?: number;
}

const TemperatureForm = ({ selectedData, patientID, toggleView, unit, unitSelector }: IProps) => {
  const dispatch = useDispatch();
  const { error, isPhrLoading } = useSelector((state: AppState) => state.phrPatientState);
  const [isSubmitting, setSubmitting] = useState<boolean>(false);

  const onSubmit = async (values: IForm) => {
    const obj = Object.assign({}, values);
    obj.temperature = convertTemperature(
      false,
      values.temperature,
      TemperatureTypeEnum.Celsius,
      unit,
    );
    setSubmitting(true);
    if (selectedData) {
      selectedData.trackerDate = moment(obj.trackerDate).toDate();
      selectedData.temperature = obj.temperature;
      await dispatch(
        updateForPhrSection(ReduxPHRCategoryEnum.PHRTemperatures, selectedData, patientID, true),
      );
    } else {
      await dispatch(
        createForPhrSection(ReduxPHRCategoryEnum.PHRTemperatures, obj, patientID, true),
      );
    }
  };

  const handleUnitChange = (setFieldValue, temperature, toUnit) => {
    setFieldValue('temperature', convertTemperature(true, temperature, toUnit, unit));
  };

  useEffect(() => {
    if (isSubmitting && !isPhrLoading) {
      if (error) {
        setSubmitting(false);
      } else {
        toggleView();
      }
    }
  }, [error, isPhrLoading, isSubmitting, toggleView]);

  const initialValues: IForm = {
    trackerDate: selectedData ? moment(selectedData.trackerDate!) : moment(),
    temperature: selectedData ? selectedData.temperature : undefined,
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={object({
        trackerDate: date().required('Date is required').typeError('Invalid date'),
        temperature: number()
          .required('Temperature is required')
          .positive('Temperature must be a postive number')
          .typeError('Temperature must be a postive number')
          .moreThan(0, 'Value must be greater than 0')
          .lessThan(150, 'Value must be less than 150'),
      })}
      onSubmit={onSubmit}
    >
      {({ status, setFieldValue, values }) => (
        <Form error={error}>
          {isSubmitting ? (
            <CenteredLoadingIndicator />
          ) : (
            <>
              <ServerValidationBox message={status} />
              <div className="row">
                <div className="col-12 d-flex justify-content-end">
                  {unitSelector((x) => {
                    handleUnitChange(setFieldValue, values.temperature, x);
                  })}
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-sm-6">
                  <FormMobisPicker
                    name="trackerDate"
                    label="Tracked at"
                    controls={['calendar', 'time']}
                    max={moment().toDate()}
                    min={moment().add(-150, 'year').toDate()}
                  />
                  {/* <FormDateTimePicker
                    name="trackerDate"
                    label="Tracked at"
                    showTimeSelect
                    maxDate={moment().toDate()}
                    minDate={moment().add(-150, 'year').toDate()}
                  /> */}
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-sm-6">
                  <FormInputControl name="temperature" label="Temperature" type="text" />
                </div>
              </div>
              <ButtonRow>
                <SecondaryButton size={ButtonSizeEnum.medium} onClick={toggleView}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton size={ButtonSizeEnum.medium} type="submit">
                  Save
                </PrimaryButton>
              </ButtonRow>
            </>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default TemperatureForm;
